import { crudOperations, tokenManager } from './api';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.listeners = [];
  }

  // Login with email and password
  async login(email, password) {
    try {
      const response = await crudOperations.post('/auth/login', {
        email,
        password
      });

      const { user, tokens } = response.data;
      
      // Store tokens
      tokenManager.setTokens(tokens.accessToken, tokens.refreshToken);
      
      // Set current user
      this.currentUser = user;
      
      // Notify listeners
      this.notifyListeners(user);
      
      return { user, success: true };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Logout - with option to preserve admin return session
  async logout(clearAdminSession = false) {
    try {
      // Call logout endpoint (optional, as JWT is stateless)
      await crudOperations.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error (continuing anyway):', error);
    } finally {
      // Clear tokens and user state
      tokenManager.clearTokens();
      this.currentUser = null;
      
      // Only clear admin return session if explicitly requested
      if (clearAdminSession) {
        localStorage.removeItem('admin_return_session');
      }
      
      this.notifyListeners(null);
    }
  }

  // Get current user from token
  async getCurrentUser() {
    if (!tokenManager.hasValidToken()) {
      return null;
    }

    try {
      const response = await crudOperations.get('/auth/verify');
      const user = response.data.user;
      
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('Get current user error:', error);
      // Token might be invalid, clear it
      tokenManager.clearTokens();
      this.currentUser = null;
      
      // Don't clear admin return session on token errors
      // The admin session should persist until explicitly cleared
      
      return null;
    }
  }

  // Admin login as user - actually login as the user with admin return capability
  async loginAsUser(targetUser) {
    try {
      // Store current admin session for return
      const adminSession = {
        user: { ...this.currentUser },
        token: tokenManager.getToken(),
        refreshToken: tokenManager.getRefreshToken(),
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem('admin_return_session', JSON.stringify(adminSession));
      
      // Actually login as the target user (no password needed for admin)
      const response = await crudOperations.post('/auth/admin/login-as', {
        targetUserId: targetUser.id
      });

      const { user, tokens } = response.data;
      
      // Store the user's actual tokens
      tokenManager.setTokens(tokens.accessToken, tokens.refreshToken);
      
      // Update current user
      this.currentUser = user;
      
      // Notify listeners
      this.notifyListeners(user);
      
      return { user, success: true };
    } catch (error) {
      console.error('Login as user error:', error);
      throw error;
    }
  }

  // Return to admin - restore admin session
  async returnToAdmin() {
    try {
      const adminSession = localStorage.getItem('admin_return_session');
      if (!adminSession) {
        throw new Error('No admin session found to return to');
      }
      
      const { user, token, refreshToken } = JSON.parse(adminSession);
      
      // Restore admin tokens and user
      tokenManager.setTokens(token, refreshToken);
      this.currentUser = user;
      
      // Clear the stored session
      localStorage.removeItem('admin_return_session');
      
      // Notify listeners
      this.notifyListeners(user);
      
      return { success: true };
    } catch (error) {
      console.error('Return to admin error:', error);
      throw error;
    }
  }

  // Check if user has specific role
  hasRole(requiredRole) {
    if (!this.currentUser) return false;
    
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(this.currentUser.role);
    }
    
    return this.currentUser.role === requiredRole;
  }

  // Check if user is admin
  isAdmin() {
    return this.hasRole('admin');
  }

  // Check if admin is logged in as another user (has return session)
  hasAdminReturn() {
    return localStorage.getItem('admin_return_session') !== null;
  }

  // Get franchisee ID for current user
  getFranchiseeId() {
    if (!this.currentUser) return null;
    return this.currentUser.franchiseeId;
  }

  // Auth state change listeners (JWT-based authentication)
  onAuthStateChanged(callback) {
    this.listeners.push(callback);
    
    // Immediately call with current user
    callback(this.currentUser);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners of auth state change
  notifyListeners(user) {
    this.listeners.forEach(listener => {
      try {
        listener(user);
      } catch (error) {
        console.error('Auth listener error:', error);
      }
    });
  }

  // Initialize auth state (check for existing valid token)
  async initializeAuth() {
    try {
      const user = await this.getCurrentUser();
      if (user) {
        this.notifyListeners(user);
      }
      return user;
    } catch (error) {
      console.error('Initialize auth error:', error);
      return null;
    }
  }

  // Refresh token manually
  async refreshToken() {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await crudOperations.post('/auth/refresh', {
        refreshToken
      });

      const { accessToken } = response.data;
      tokenManager.setTokens(accessToken);
      
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      tokenManager.clearTokens();
      this.logout(false); // Don't clear admin session on token refresh failure
      throw error;
    }
  }

  // Get current auth token (for API calls)
  getToken() {
    return tokenManager.getToken();
  }
}

// Create singleton instance
const authService = new AuthService();

// Export both the service and some utility functions
export { authService as default };

// Compatibility functions for consistent API interface
export const signInWithEmail = (email, password) => authService.login(email, password);
export const signOutUser = () => authService.logout(true); // Clear admin session on explicit sign out
export const onAuthStateChanged = (callback) => authService.onAuthStateChanged(callback);

// New JWT-specific functions
export const signInWithCustomTokenAPI = (token) => {
  tokenManager.setTokens(token);
  return authService.getCurrentUser();
};